/**
 * React hook that wires activity tracking into the app lifecycle.
 *
 * - Tracks page_view on route changes (with dedup)
 * - Tracks feature_first_use on first navigation to each module
 * - Starts/stops the flush timer
 * - Flushes on visibility change (tab hidden / page unload)
 *
 * Must be called inside RouterProvider (needs useLocation).
 * Designed for MainLayout — covers ALL authenticated routes.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, startTracker, stopTracker, flushNow } from './tracker';

/** Modules that trigger feature_first_use events. */
const MODULE_PREFIXES: Record<string, string> = {
  '/crm': 'crm',
  '/insights': 'insights',
  '/inbox': 'inbox',
  '/storefront': 'storefront',
  '/item': 'products',
  '/customers': 'customers',
  '/orders': 'orders',
};

function checkFirstUse(pathname: string): void {
  try {
    for (const [prefix, module] of Object.entries(MODULE_PREFIXES)) {
      if (pathname.startsWith(prefix)) {
        const key = `zvg_first_use_${module}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, '1');
          trackEvent({
            event_type: 'feature_first_use',
            page_path: pathname,
            metadata: { module },
          });
        }
        return;
      }
    }
  } catch {
    // Silent
  }
}

export function useActivityTracker(): void {
  const location = useLocation();
  const prevPathRef = useRef<string>('');

  // Start/stop tracker + visibility handler
  useEffect(() => {
    startTracker();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushNow({ keepalive: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopTracker();
    };
  }, []);

  // Track page views on route change
  useEffect(() => {
    const referrer = prevPathRef.current;
    prevPathRef.current = location.pathname;

    trackEvent({
      event_type: 'page_view',
      page_path: location.pathname,
      metadata: referrer ? { referrer_path: referrer } : undefined,
    });

    // Check first use of module sections
    checkFirstUse(location.pathname);
  }, [location.pathname]);
}
